/**
 * Générateur — Repérer un nombre décimal sur une demi-droite graduée
 * Attendu ID : 603 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : qcm_unique, texte (saisie), tableau_vf, relier
 * Labels de référence :
 *   - Lire l'abscisse d'un point (graduation en dixièmes)
 *   - Lire l'abscisse d'un point (graduation en centièmes)
 *   - Lire l'abscisse d'un point (graduation grandes valeurs)
 * Variantes estimées : >2000
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier,
    basePayload,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 603;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const LETTRES_POINTS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

// ─── Helpers spécifiques ───

// Fraction verticale d'une ligne KaTeX globale avec ses deux trous (audit
// fractions en texte, 2026-09-06) : `\rule` invisibles pour la hauteur des
// slots, triple accolade `{{{X}}}` décodée par preprocessBraces (motif 620-622).
const fracTrous = (num, den) => `\\dfrac{{{${num}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${den}}}}`;

const round = (n, decimals) => {
    const factor = Math.pow(10, decimals);
    return Math.round(n * factor) / factor;
};

const toFr = (n) => {
    const s = Number(n.toFixed(3)).toString();
    return s.replace('.', ',');
};

/** Génère une config de droite aléatoire selon le type */
const makeLineConfig = (type) => {
    switch (type) {
        case 'dixiemes': {
            const startInt = randInt(0, 15);
            const span = randomChoice([1, 2, 3]);
            return { start: startInt, end: startInt + span, step: 0.1, decimals: 1 };
        }
        case 'centiemes': {
            const bases = [0, 0.1, 0.5, 0.9, 1.5, 2.9, 5.4, 6.41, 7.8, 9.9];
            const base = randomChoice(bases);
            return { start: base, end: round(base + 0.1, 2), step: 0.01, decimals: 2 };
        }
        case 'grandes_valeurs': {
            const configs = [
                { start: 0, end: 100, step: 10, decimals: 0 },
                { start: 0, end: 1000, step: 100, decimals: 0 },
                { start: 0, end: 50, step: 5, decimals: 0 },
            ];
            return randomChoice(configs);
        }
        default:
            return { start: 0, end: 2, step: 0.1, decimals: 1 };
    }
};

/** Génère N points distincts sur la droite (hors bornes) */
const generatePointValues = (start, end, step, n, decimals) => {
    const totalSteps = Math.round((end - start) / step);
    const candidates = [];
    for (let i = 1; i < totalSteps; i++) {
        candidates.push(round(start + i * step, decimals));
    }
    if (candidates.length < n) return candidates;
    return randomChoiceMultiple(candidates, n).sort((a, b) => a - b);
};

/** Construit l'objet numberline pour elements_additionnels avec des points marqués */
const makeNumberline = (start, end, step, pointsData) => ({
    version: 1,
    elements: [{
        type: 'numberline',
        config: {
            start, end, step,
            lineType: 'half',
            showArrow: true,
            showStart: true,
            showEnd: true,
            points: pointsData.map(p => ({
                value: p.value,
                label: p.label,
                showValue: false,
                color: '#22c55e',
            })),
        },
    }],
});

// ─── Labels ───
const L_DIX = 'Lire l\'abscisse d\'un point (graduation en dixièmes)';
const L_CENT = 'Lire l\'abscisse d\'un point (graduation en centièmes)';
const L_GRAND = 'Lire l\'abscisse d\'un point (graduation grandes valeurs)';
const L_FORMES = 'Écrire l\'abscisse sous différentes formes (décimale, fraction, somme)';

// ─── Générateurs QCM ───

/**
 * QCM Dixièmes : une droite, un point, 4 choix.
 * Distracteurs : compter les traits, confondre le pas, ±1 graduation, lire depuis la fin.
 */
/**
 * RÈGLES DES DISTRACTEURS QCM — documentation machine-lisible, NON utilisée à
 * l'exécution. C'est le CONTRAT à vérifier lors de toute revue (LLM ou humaine)
 * des trois générateurs QCM ci-dessous : chaque distracteur est une ERREUR
 * NOMMÉE d'élève (sources : CSEN, IREM, éduscol), jamais du bruit.
 * Arbitrages : revue pédagogique avec le prof (décideur didactique), 2026-07-26.
 */
export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'exactement 3 distracteurs + la bonne réponse, tous distincts (Map à clés uniques, filtre contre la correcte)',
        "au plus UNE valeur hors de [début, fin] de la droite par tirage, jugée sur la VALEUR (pas le code) : deux valeurs aberrantes s'éliminent d'un coup d'œil et ramènent le QCM à un 50/50",
        "aucun distracteur muet : le repli « voisin » (±2/3/4 crans, borné DANS la droite) ne sort qu'en cas de collision",
        'chaque distracteur porte son code dans _meta.params.distracteurs (futur feedback pédagogique nommant l\'erreur)',
        "SUPPRIMÉS par arbitrage — ne pas réintroduire : moins_un_cran (jumeau pauvre du +1 cran), pas_double sur droite 0,1 (pas d'habitat : les entiers consécutifs sont visibles), ancrage_inverse (les entiers intermédiaires ne sont pas étiquetés sur la figure), origine_oubliee en dixièmes (l'élève qui ignore l'origine ne convertit pas non plus en dixièmes : il pose l'entier compté)",
    ],
    lire_dixiemes_qcm_unique: {
        support: "droite en 0,1 (~4 tirages sur 5) OU droite piège en 0,2/0,5 (~1 sur 5)",
        fixes: {
            plus_un_cran: "correct + pas : il compte le trait de départ comme « 1 » (erreur de comptage dominante)",
            cascade_3e_slot: "rang >= 10 → virgule_compteur « start,rang » (15,12 pour le 12e trait après 15 : le décimal vu comme deux entiers collés — n'existe que si la droite couvre au moins 2 unités) ; sinon → centiemes, chiffre glissé d'un rang (15,4 → 15,04)",
            habitude_dixieme: "droites pièges 0,2/0,5 seulement : start + rang × 0,1 (il lit 0,1 par habitude) — TOUJOURS présent sur ces droites",
        },
        rotation: "1 slot : comptage_unite = start + rang (il compte 15, 16, 17, 18, 19…) OU comptage_brut = rang (il compte les traits « 1, 2, 3… » et pose l'entier)",
    },
    lire_centiemes_qcm_unique: {
        support: "droite de longueur 0,1 graduée en 0,01",
        fixes: {
            plus_un_cran: "correct + 0,01",
            habitude_dixieme: "start + rang × 0,1 (1,5 + 9 traits = 2,4 : l'unité réflexe est le dixième) — la seule valeur hors droite du tirage, par construction",
            confusion_rang: "chiffre glissé d'un rang : 1,59 → 1,509",
        },
        rotation: "aucune : les 3 familles sont toujours définies",
    },
    lire_grandes_valeurs_qcm_unique: {
        support: "0→50 pas 5 · 0→100 pas 10 · 0→1000 pas 100",
        fixes: {
            plus_un_cran: "correct + pas",
        },
        rotation: {
            comptage_unite: "rang nu (« 7 » pour le 7e trait)",
            habitude_pas10: "rang × 10 (pas 5 : 35 lu 70 ; pas 100 : 600 lu 60) — AUTO-DÉSACTIVÉE sur pas 10 (rang × 10 = la correcte, filtrée par construction)",
            zero_en_trop: "correct × 10 (40 → 400, erreur de valeur de position)",
            zero_en_moins: "correct ÷ 10 si entier (300 → 30)",
        },
    },
};

const genLireDixQcm = () => {
    // Support (arbitrage 2026-07-26) : ~4 tirages sur 5 en pas 0,1 ; ~1 sur 5 en
    // pas « piège » 0,2 ou 0,5 — l'habitat naturel de l'erreur d'échelle est la
    // droite NON graduée en 0,1 que l'élève lit en 0,1 par habitude.
    const pasPiege = randInt(1, 10) > 8;
    let config;
    if (pasPiege) {
        const pas = randomChoice([0.2, 0.5]);
        const startInt = randInt(0, 15);
        const span = pas === 0.5 ? randomChoice([2, 3]) : randomChoice([1, 2, 3]);
        config = { start: startInt, end: startInt + span, step: pas, decimals: 1 };
    } else {
        config = makeLineConfig('dixiemes');
    }
    const label = randomChoice(LETTRES_POINTS);
    const values = generatePointValues(config.start, config.end, config.step, 1, config.decimals);
    const correct = values[0];
    const correctFr = toFr(correct);

    // Distracteurs ciblés — 1 FIXE (l'erreur dominante) + 2 EN ROTATION dans un
    // pool d'erreurs nommées. Chaque valeur matérialise une erreur précise ;
    // les codes sont gardés dans _meta.params pour un futur feedback pédagogique.
    // La rotation évite le méta-jeu (« le grand entier est toujours faux »).
    const rang = Math.round((correct - config.start) / config.step);
    // FIXE — décalage d'un cran : a compté la graduation de départ comme 1
    const plusUnCran = round(correct + config.step, 1);
    const pool = [];
    // FIXE (chemin 2 uniquement) — le roi de ce support : lecture en 0,1 par
    // habitude (start + rang × 0,1). Toujours présent, hors rotation.
    const habitudeDixieme = pasPiege ? round(config.start + rang * 0.1, 1) : null;
    if (pasPiege) {
        pool.push({ code: 'comptage_unite', valeur: round(config.start + rang, 0) });
        pool.push({ code: 'comptage_brut', valeur: rang });
    } else {
        // comptage de graduation à l'unité depuis l'étiquette de départ : 15, 16, 17, 18, 19…
        pool.push({ code: 'comptage_unite', valeur: round(config.start + rang, 0) });
        // comptage brut : il compte les traits « 1, 2, 3… » et pose l'entier, sans
        // origine ni dixièmes (les deux savoirs manquent ENSEMBLE — arbitrage 2026-07-26)
        pool.push({ code: 'comptage_brut', valeur: rang });
    }

    // 3e slot FIXE du chemin 1 — la famille « chiffres après la virgule sans valeur
    // de rang » (misconception documentée : le décimal vu comme deux entiers collés).
    // Cascade : point au-delà de l'entier suivant → virgule-compteur (« 15,12 » pour
    // le 12e trait après 15) ; sinon confusion de rang (« 15,04 » pour 15,4).
    let cascade = null;
    if (!pasPiege) {
        if (rang >= 10) {
            cascade = { code: 'virgule_compteur', fr: `${config.start},${rang}`, valeur: config.start + rang / 100 };
        } else {
            const dec = round(correct - Math.floor(correct), 1);
            const valCent = round(Math.floor(correct) + dec / 10, 2);
            cascade = { code: 'centiemes', fr: toFr(valCent), valeur: valCent };
        }
    }

    // Au plus UN distracteur « hors droite » par tirage, ÉVALUÉ SUR LA VALEUR
    // (pas sur le code) : deux valeurs hors de [début, fin] s'éliminent d'un coup
    // d'œil par l'ordre de grandeur et ramènent le QCM à un 50/50 sur les proches.
    // (Le pas double dépasse la fin dès que le point passe le milieu de la droite ;
    // à l'inverse un comptage à l'unité peut tomber DANS la droite — dynamique.)
    const distracteurs = new Map([[toFr(plusUnCran), 'plus_un_cran']]);
    if (habitudeDixieme !== null && toFr(habitudeDixieme) !== correctFr && !distracteurs.has(toFr(habitudeDixieme))) {
        distracteurs.set(toFr(habitudeDixieme), 'habitude_dixieme');
    }
    if (cascade !== null && cascade.fr !== correctFr && !distracteurs.has(cascade.fr)) {
        distracteurs.set(cascade.fr, cascade.code);
    }
    let horsDroitePris = false;
    for (const c of shuffle(pool)) {
        if (distracteurs.size >= 3) break;
        const estHorsDroite = c.valeur < config.start || c.valeur > config.end;
        if (estHorsDroite && horsDroitePris) continue;
        const fr = toFr(c.valeur);
        if (fr !== correctFr && !distracteurs.has(fr)) {
            distracteurs.set(fr, c.code);
            if (estHorsDroite) horsDroitePris = true;
        }
    }
    // Repli déterministe : voisins à ±2/3/4 crans, TOUJOURS dans la droite
    // (une valeur qui déborde compterait comme 2e « hors droite » → éliminable).
    for (let k = 2; distracteurs.size < 3 && k <= 4; k++) {
        for (const v of [round(correct + k * config.step, 1), round(correct - k * config.step, 1)]) {
            if (distracteurs.size >= 3) break;
            if (v < config.start || v > config.end) continue;
            const fr = toFr(v);
            if (fr !== correctFr && !distracteurs.has(fr)) distracteurs.set(fr, 'voisin');
        }
    }

    const choix = [correctFr, ...distracteurs.keys()];

    const elAdd = makeNumberline(config.start, config.end, config.step, [{ value: correct, label }]);
    const payload = makeQcm(choix, correctFr,
        `<p>Quelle est l'abscisse du point ${label} ?</p>{{element:0}}`,
        { ...opts(L_DIX), elements_additionnels: elAdd },
    );
    payload._meta.params = { correct, start: config.start, pas: config.step, distracteurs: Object.fromEntries(distracteurs) };
    return payload;
};

/** QCM Centièmes */
const genLireCentQcm = () => {
    const config = makeLineConfig('centiemes');
    const label = randomChoice(LETTRES_POINTS);
    const values = generatePointValues(config.start, config.end, config.step, 1, config.decimals);
    if (values.length === 0) return genLireDixQcm(); // fallback
    const correct = values[0];
    const correctFr = toFr(correct);

    // Grille arbitrée 2026-07-26 (même logique que le QCM dixièmes) :
    // 3 erreurs nommées, au plus une valeur hors droite, codes gardés en _meta.
    const rang = Math.round((correct - config.start) / config.step);
    const distracteurs = new Map();
    // FIXE — décalage d'un cran : a compté la graduation de départ (+0,01)
    distracteurs.set(toFr(round(correct + 0.01, 3)), 'plus_un_cran');
    // FIXE — comptage en DIXIÈMES par habitude : sur une droite en centièmes,
    // l'unité réflexe de l'élève est le dixième → 1,5 + 9 traits = 2,4 (la seule
    // valeur hors droite du tirage : le quota max-1 est structurel ici)
    const habitude = toFr(round(config.start + rang * 0.1, 2));
    if (habitude !== correctFr && !distracteurs.has(habitude)) distracteurs.set(habitude, 'habitude_dixieme');
    // FIXE — confusion de rang : chiffre glissé d'un rang (1,59 → 1,509)
    const parts = correct.toFixed(config.decimals).split('.');
    if (parts[1] && parts[1].length === 2) {
        const glisse = toFr(parseFloat(parts[0] + '.' + parts[1][0] + '0' + parts[1][1]));
        if (glisse !== correctFr && !distracteurs.has(glisse)) distracteurs.set(glisse, 'confusion_rang');
    }
    // Repli déterministe : voisins au centième, bornés dans la droite
    for (let k = 2; distracteurs.size < 3 && k <= 4; k++) {
        for (const v of [round(correct + k * config.step, 3), round(correct - k * config.step, 3)]) {
            if (distracteurs.size >= 3) break;
            if (v < config.start || v > config.end) continue;
            const fr = toFr(v);
            if (fr !== correctFr && !distracteurs.has(fr)) distracteurs.set(fr, 'voisin');
        }
    }

    const choix = [correctFr, ...distracteurs.keys()];

    const elAdd = makeNumberline(config.start, config.end, config.step, [{ value: correct, label }]);
    const payload = makeQcm(choix, correctFr,
        `<p>Quelle est l'abscisse du point ${label} ?</p>{{element:0}}`,
        { ...opts(L_CENT), elements_additionnels: elAdd },
    );
    payload._meta.params = { correct, start: config.start, pas: config.step, distracteurs: Object.fromEntries(distracteurs) };
    return payload;
};

/** QCM Grandes valeurs */
const genLireGrandQcm = () => {
    const config = makeLineConfig('grandes_valeurs');
    const label = randomChoice(LETTRES_POINTS);
    const values = generatePointValues(config.start, config.end, config.step, 1, config.decimals);
    if (values.length === 0) return genLireDixQcm();
    const correct = values[0];
    const correctFr = toFr(correct);

    // Grille arbitrée 2026-07-26 (même logique que dixièmes/centièmes) : 1 fixe +
    // rotation d'erreurs nommées, au plus une valeur hors droite, codes en _meta.
    const rang = Math.round((correct - config.start) / config.step);
    // FIXE — décalage d'un cran : a compté la graduation de départ
    const distracteurs = new Map([[toFr(correct + config.step), 'plus_un_cran']]);
    const pool = [
        // comptage à l'unité : il compte les traits « 1, 2, 3… » et pose le rang
        { code: 'comptage_unite', valeur: rang },
        // lecture au pas de 10 par habitude (pas 5 : 40 lu 80 ; pas 100 : 300 lu 30) —
        // sur les droites en pas 10, rang×10 = la correcte → auto-filtré plus bas
        { code: 'habitude_pas10', valeur: rang * 10 },
        // valeur de position : un zéro en trop (40 → 400)
        { code: 'zero_en_trop', valeur: correct * 10 },
        // valeur de position : un zéro en moins (300 → 30) — entiers seulement
        ...(Number.isInteger(correct / 10) ? [{ code: 'zero_en_moins', valeur: correct / 10 }] : []),
    ];
    let horsDroitePris = false;
    for (const c of shuffle(pool)) {
        if (distracteurs.size >= 3) break;
        const estHorsDroite = c.valeur < config.start || c.valeur > config.end;
        if (estHorsDroite && horsDroitePris) continue;
        const fr = toFr(c.valeur);
        if (fr !== correctFr && !distracteurs.has(fr)) {
            distracteurs.set(fr, c.code);
            if (estHorsDroite) horsDroitePris = true;
        }
    }
    // Repli déterministe : voisins au pas, bornés dans la droite
    for (let k = 2; distracteurs.size < 3 && k <= 4; k++) {
        for (const v of [correct + k * config.step, correct - k * config.step]) {
            if (distracteurs.size >= 3) break;
            if (v < config.start || v > config.end) continue;
            const fr = toFr(v);
            if (fr !== correctFr && !distracteurs.has(fr)) distracteurs.set(fr, 'voisin');
        }
    }

    const choix = [correctFr, ...distracteurs.keys()];

    const elAdd = makeNumberline(config.start, config.end, config.step, [{ value: correct, label }]);
    const payload = makeQcm(choix, correctFr,
        `<p>Quelle est l'abscisse du point ${label} ?</p>{{element:0}}`,
        { ...opts(L_GRAND), elements_additionnels: elAdd },
    );
    payload._meta.params = { correct, start: config.start, pas: config.step, distracteurs: Object.fromEntries(distracteurs) };
    return payload;
};

// ─── Générateurs Saisie ───

/** Saisie Dixièmes */
const genLireDixSaisie = () => {
    const config = makeLineConfig('dixiemes');
    const label = randomChoice(LETTRES_POINTS);
    const values = generatePointValues(config.start, config.end, config.step, 1, config.decimals);
    const correct = values[0];

    const elAdd = makeNumberline(config.start, config.end, config.step, [{ value: correct, label }]);
    return makeSaisie(toFr(correct),
        `<p>Lis l'abscisse du point ${label} sur la demi-droite graduée ci-dessous.</p>{{element:0}}`,
        { ...opts(L_DIX), strict: false, elements_additionnels: elAdd },
    );
};

/** Saisie Centièmes */
const genLireCentSaisie = () => {
    const config = makeLineConfig('centiemes');
    const label = randomChoice(LETTRES_POINTS);
    const values = generatePointValues(config.start, config.end, config.step, 1, config.decimals);
    if (values.length === 0) return genLireDixSaisie();
    const correct = values[0];

    const elAdd = makeNumberline(config.start, config.end, config.step, [{ value: correct, label }]);
    return makeSaisie(toFr(correct),
        `<p>Lis l'abscisse du point ${label} sur la demi-droite graduée ci-dessous.</p>{{element:0}}`,
        { ...opts(L_CENT), strict: false, elements_additionnels: elAdd },
    );
};

// ─── Générateur Vrai/Faux ───

/** V/F avec 3 points sur une droite en dixièmes */
const genLireVF = () => {
    const config = makeLineConfig('dixiemes');
    const labels = randomChoiceMultiple(LETTRES_POINTS, 3);
    const values = generatePointValues(config.start, config.end, config.step, 3, config.decimals);
    if (values.length < 3) return genLireDixQcm(); // fallback

    const pointsData = values.map((v, i) => ({ value: v, label: labels[i] }));
    const elNumberline = makeNumberline(config.start, config.end, config.step, pointsData);

    // Pour chaque point, soit on donne la bonne abscisse (Vrai), soit une fausse (Faux)
    const lignes = [];
    const bonnesReponses = [];

    labels.forEach((lbl, i) => {
        const isTrue = Math.random() < 0.5;
        if (isTrue) {
            lignes.push(`${lbl} a pour abscisse ${toFr(values[i])}`);
            bonnesReponses.push(0); // Vrai
        } else {
            // Fausse abscisse : ±1 ou ±2 graduations
            const offset = randomChoice([-1, 1, -2, 2]);
            let fausse = round(values[i] + offset * config.step, config.decimals);
            // S'assurer que la fausse valeur est dans la plage
            if (fausse <= config.start || fausse >= config.end) {
                fausse = round(values[i] - offset * config.step, config.decimals);
            }
            lignes.push(`${lbl} a pour abscisse ${toFr(fausse)}`);
            bonnesReponses.push(1); // Faux
        }
    });

    // S'assurer qu'on a au moins 1 Vrai et 1 Faux
    if (bonnesReponses.every(r => r === 0) || bonnesReponses.every(r => r === 1)) {
        bonnesReponses[1] = bonnesReponses[1] === 0 ? 1 : 0;
        if (bonnesReponses[1] === 1) {
            const offset = randomChoice([-1, 1]);
            const fausse = round(values[1] + offset * config.step, config.decimals);
            lignes[1] = `${labels[1]} a pour abscisse ${toFr(fausse)}`;
        } else {
            lignes[1] = `${labels[1]} a pour abscisse ${toFr(values[1])}`;
        }
    }

    // Construire le payload V/F avec numberline en plus
    const colonnes = ['Vrai', 'Faux'];
    const br = lignes.map((ligne, i) => `[${ligne}|${colonnes[bonnesReponses[i]]}]`).join('');

    return basePayload('qcm',
        `<p>Observe la demi-droite graduée puis indique si chaque affirmation est vraie ou fausse.</p>{{element:0}}{{element:1}}`,
        { choix: [], bonne_reponse: br },
        opts(L_DIX),
        {
            version: 1,
            elements: [
                elNumberline.elements[0],
                {
                    type: 'table',
                    config: {
                        type: 'vraiFaux',
                        colonnes,
                        lignes,
                        bonnesReponses,
                    },
                },
            ],
        },
    );
};

// ─── Générateur Relier ───

/** Relier : 4 points sur une droite, relier chaque lettre à son abscisse */
// Gabarit anti-élimination (arbitrage 2026-07-26, kit figé au bilan prof D3 du
// 2026-07-27) : 2 POINTS / 6 CIBLES — les 2 vraies + 4 leurres nommés :
//   · decalage_cran : le décalé +1 cran (même sens) de CHAQUE point — l'élève
//     qui compte le trait de départ matérialise SES DEUX fausses lectures ;
//   · comptage_unite : le comptage de CHAQUE point, même grille que les QCM du
//     gen (dixièmes : start + rang, « il compte 15, 16, 17… » ; grandes
//     valeurs : le rang nu, « il compte les traits 1, 2, 3… »).
// Pas de quota hors-droite ici (≠ QCM) : les leurres ne sont pas des choix
// exclusifs, l'élimination par ordre de grandeur n'offre aucune paire gratuite
// puisque les cibles restent surnuméraires — et le compteur doit TROUVER sa
// valeur pour être diagnostiqué. Repli (collisions) : pas double, décalé −1.
const genLireRelier = () => {
    const typeChoice = randomChoice(['dixiemes', 'grandes_valeurs']);
    const config = makeLineConfig(typeChoice);
    const labels = randomChoiceMultiple(LETTRES_POINTS, 2);
    const values = generatePointValues(config.start, config.end, config.step, 2, config.decimals);
    if (values.length < 2) return genLireDixQcm(); // fallback

    const pointsData = values.map((v, i) => ({ value: v, label: labels[i] }));
    const elAdd = makeNumberline(config.start, config.end, config.step, pointsData);

    const pairs = labels.map((lbl, i) => ({
        source: lbl,
        target: toFr(values[i]),
    }));

    const step = config.step;
    const dec = config.decimals + 1; // marge d'arrondi (les valeurs restent sur la grille du pas)
    const rangs = values.map(v => Math.round((v - config.start) / step));
    const comptage = (i) => (typeChoice === 'dixiemes'
        ? round(config.start + rangs[i], 0)
        : rangs[i]);
    const candidats = [
        { v: round(values[0] + step, dec), code: 'decalage_cran' },
        { v: round(values[1] + step, dec), code: 'decalage_cran' },
        { v: comptage(0), code: 'comptage_unite' },
        { v: comptage(1), code: 'comptage_unite' },
        // Repli si collision (valeurs proches, comptage tombant sur une cible…)
        { v: round(config.start + 2 * (values[0] - config.start), dec), code: 'pas_double' },
        { v: round(config.start + 2 * (values[1] - config.start), dec), code: 'pas_double' },
        { v: round(values[0] - step, dec), code: 'decalage_cran' },
        { v: round(values[1] - step, dec), code: 'decalage_cran' },
    ];
    const pris = new Set(pairs.map(p => p.target));
    const leurres = [];
    for (const c of candidats) {
        if (leurres.length >= 4) break;
        const fr = toFr(c.v);
        if (pris.has(fr)) continue;
        pris.add(fr);
        leurres.push({ target: fr, code: c.code });
    }

    const labelText = typeChoice === 'dixiemes' ? L_DIX : L_GRAND;

    const payload = makeRelier(pairs,
        `<p>Relie chaque point à son abscisse. Attention : certaines étiquettes ne correspondent à aucun point.</p>{{element:0}}`,
        { ...opts(labelText), cibles_leurres: leurres, elements_additionnels: elAdd });
    payload._meta.params = {
        pas: step, valeurs: values,
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// ─── Générateur Formes d'écriture ───

/**
 * Texte à trous (saisie) : 3 points sur une droite en dixièmes,
 * une seule forme demandée (choisie aléatoirement) :
 *   - écriture décimale : O(11,3)
 *   - somme entier + fraction décimale : K(16 + 3/10)
 *   - fraction décimale : J(113/10)
 */
const genLireFormes = () => {
    const labels = randomChoiceMultiple(LETTRES_POINTS, 3);
    const startInt = randInt(1, 15);
    const span = randomChoice([2, 3]);
    const start = startInt;
    const end = startInt + span;
    const step = 0.1;

    // Générer 3 points non-entiers (on veut des dixièmes pour les fractions)
    const totalSteps = Math.round((end - start) / step);
    const candidates = [];
    for (let i = 1; i < totalSteps; i++) {
        const val = round(start + i * step, 1);
        if (!Number.isInteger(val)) candidates.push(val);
    }
    if (candidates.length < 3) return genLireDixQcm(); // fallback improbable
    const values = randomChoiceMultiple(candidates, 3).sort((a, b) => a - b);

    const pointsData = values.map((v, i) => ({ value: v, label: labels[i] }));
    const elAdd = makeNumberline(start, end, step, pointsData);

    // Choisir UNE forme au hasard pour tout l'exercice
    const form = randomChoice(['decimale', 'somme', 'fraction']);

    let consigne;
    const parts = [];

    if (form === 'decimale') {
        consigne = 'Donner l\'abscisse de chaque point en écriture décimale.';
        labels.forEach((lbl, i) => {
            parts.push(`${lbl}({{${toFr(values[i])}}})`);
        });
    } else if (form === 'somme') {
        consigne = 'Donner l\'abscisse de chaque point comme la somme d\'un nombre entier et d\'une fraction décimale inférieure à 1.';
        // Lignes KaTeX globales : la fraction est verticale, ses deux trous
        // dans le numérateur et le dénominateur (audit fractions en texte,
        // 2026-09-06 ; motif 620-622 pour les `\rule` invisibles).
        labels.forEach((lbl, i) => {
            const partEntiere = Math.floor(values[i]);
            const dixieme = Math.round((values[i] - partEntiere) * 10);
            parts.push(`$${lbl}\\left({{${partEntiere}}} + ${fracTrous(dixieme, 10)}\\right)$`);
        });
    } else {
        consigne = 'Donner l\'abscisse de chaque point sous la forme d\'une fraction décimale.';
        labels.forEach((lbl, i) => {
            const partEntiere = Math.floor(values[i]);
            const dixieme = Math.round((values[i] - partEntiere) * 10);
            const num = partEntiere * 10 + dixieme;
            parts.push(`$${lbl}\\left(${fracTrous(num, 10)}\\right)$`);
        });
    }

    const phrase = parts.join('\n');

    return basePayload('association',
        `<p>${consigne}</p>{{element:0}}`,
        {
            subType: 'texte_trou',
            phrase,
            mode: 'saisie',
            distracteurs: [],
            strict: false,
            bonne_reponse: phrase,
        },
        opts(L_FORMES),
        elAdd,
    );
};

// ─── Registre ───
const GENERATORS = {
    lire_dixiemes_qcm_unique: genLireDixQcm,
    lire_dixiemes_texte: genLireDixSaisie,
    lire_dixiemes_tableau_vf: genLireVF,
    lire_dixiemes_relier: genLireRelier,
    lire_centiemes_qcm_unique: genLireCentQcm,
    lire_centiemes_texte: genLireCentSaisie,
    lire_grandes_valeurs_qcm_unique: genLireGrandQcm,
    ecrire_formes_texte_trou_saisie: genLireFormes,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lire_dixiemes', label: L_DIX, formatsList: buildFormats('lire_dixiemes') },
    { id: 'lire_centiemes', label: L_CENT, formatsList: buildFormats('lire_centiemes') },
    { id: 'lire_grandes_valeurs', label: L_GRAND, formatsList: buildFormats('lire_grandes_valeurs') },
    { id: 'ecrire_formes', label: L_FORMES, formatsList: buildFormats('ecrire_formes') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
